import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Settings,
  Users,
  BarChart3,
  Box,
  FileText,
  Monitor,
  Infinity,
  ShieldCheck,
  Search,
  Code2,
  Rocket,
  GraduationCap,
  Briefcase,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const WHY_XP_SKILLS_CARDS = [
  {
    title: "Hands-on Experience",
    desc: "Work directly with robots, sensors and actuators.",
    icon: Settings,
    iconColor: "#0284C7",
    iconBg: "#E0F2FE",
  },
  {
    title: "Expert-Led Mentorship",
    desc: "Learn from industry experts with real build-test-debug cycles.",
    icon: Users,
    iconColor: "#A855F7",
    iconBg: "#FAE8FF",
  },
  {
    title: "Project-Based Learning",
    desc: "Every module is designed to build tangible real-world outcomes.",
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
  },
];

const XP_SKILLS_CAPABILITIES = [
  {
    title: "Robotics Fundamentals",
    desc: "Understand core concepts in robotics, control and AI.",
    icon: Box,
    iconColor: "#2563EB",
    iconBg: "#EBF3FF",
  },
  {
    title: "Real-World Projects",
    desc: "Work on industry-relevant projects from day one.",
    icon: FileText,
    iconColor: "#EA580C",
    iconBg: "#FEF3EB",
  },
  {
    title: "Simulation & Virtual Labs",
    desc: "Learn with NVIDIA Isaac Sim and modern simulation tools.",
    icon: Monitor,
    iconColor: "#16A34A",
    iconBg: "#ECFDF5",
  },
  {
    title: "Expert-Led Sessions",
    desc: "Get mentored by practicing robotics experts.",
    icon: Users,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
  },
  {
    title: "Hardware-in-the-Loop",
    desc: "Integrate and test with real robotics Systems.",
    icon: Infinity,
    iconColor: "#D946EF",
    iconBg: "#FDF4FF",
  },
  {
    title: "Certificate of Completion",
    desc: "Receive a verified certificate upon successful completion.",
    icon: ShieldCheck,
    iconColor: "#65A30D",
    iconBg: "#F7FEE7",
  },
];

const LEARNING_JOURNEY_STEPS = [
  {
    title: "Learn",
    desc: "Understand concepts and explore tools.",
    icon: Search,
    iconColor: "#0284C7",
    iconBg: "#CFF0FE",
    glowColor: "rgba(0, 210, 238, 0.55)",
  },
  {
    title: "Build",
    desc: "Hands-on modules and real projects.",
    icon: Settings,
    iconColor: "#A855F7",
    iconBg: "#F5D0FE",
    glowColor: "rgba(217, 70, 239, 0.5)",
  },
  {
    title: "Test & Debug",
    desc: "Validate, iterate and solve real challenges.",
    icon: Code2,
    iconColor: "#CA8A04",
    iconBg: "#FEF9C3",
    glowColor: "rgba(234, 179, 8, 0.5)",
  },
  {
    title: "Deploy",
    desc: "Work on real-world use cases and present outcomes.",
    icon: Rocket,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    glowColor: "rgba(34, 197, 94, 0.5)",
  },
];

const XP_SKILLS_STAKEHOLDERS = [
  {
    title: "Engineering Students",
    desc: "Gain hands-on experience beyond the classroom.",
    image: "/images/products/engineering-students.jpg",
    icon: GraduationCap,
  },
  {
    title: "Working Professionals",
    desc: "Build practical skills to grow your career.",
    image: "/images/products/working-professionals.jpg",
    icon: Briefcase,
  },
  {
    title: "AI/ML Engineers",
    desc: "Apply AI to real-world robotics systems.",
    image: "/images/products/aiml-engineers.jpg",
    icon: Settings,
  },
  {
    title: "Robotics Enthusiasts",
    desc: "Turn your passion into practical projects.",
    image: "/images/products/robotics-enthusiasts.jpg",
    icon: Users,
  },
];

const XP_SKILLS_PROJECTS = [
  {
    title: "Robotic Arm Pick & Place",
    image: "/images/products/robotic-arm-pick-place.jpg",
  },
  {
    title: "Autonomous Navigation (AMR)",
    image: "/images/products/autonomous-navigation-amr.jpg",
  },
  {
    title: "Humanoid Simulation",
    image: "/images/products/humanoid-simulation.jpg",
  },
  {
    title: "Quadruped Robotics",
    image: "/images/products/quadruped-robotics.jpg",
  },
];

const XpSkillsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#010A17] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Top Navigation */}
      <StackNavbar />

      <main className="flex-1">
        {/* =========================================================================
            SECTION 1: HERO SECTION: XP SKILLS (Matching Screenshot Exactly)
            Dimensions: 1440px x 617px
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden"
          style={{ backgroundColor: "#112847", minHeight: "617px" }}
        >
          {/* Background Image: Robotics Lab with Robotic Arm + Simulation Laptop */}
          <img
            src="/images/products/xp-skills-hero.jpg"
            alt="XP Skills Robotics Project Lab"
            className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none select-none z-0"
            style={{ minHeight: "617px" }}
          />

          {/* Left-side Dark Gradient Overlay from user-provided PNG */}
          <img
            src="/images/products/xp-skills-hero-overlay.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-left pointer-events-none select-none z-[1]"
          />

          {/* Main Hero Content (Left aligned, vertically centered) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] min-h-[617px] flex items-center px-6 sm:px-10 lg:px-[66px] xl:px-[72px] pt-[84px] sm:pt-[97px] pb-12 lg:pb-0">
            <div className="max-w-[580px] flex flex-col items-start">
              {/* Breadcrumbs / Eyebrow */}
              <div className="flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8] select-none">
                <Link
                  to="/what-we-do"
                  className="hover:text-white transition-colors duration-200"
                >
                  WHAT WE DO
                </Link>
                <span className="text-[#4E6888]">&gt;</span>
                <Link
                  to="/what-we-do#products"
                  className="hover:text-white transition-colors duration-200"
                >
                  PRODUCTS
                </Link>
                <span className="text-[#4E6888]">&gt;</span>
                <span className="text-[#9DB5D0]">XP SKILLS</span>
              </div>

              {/* Main Headline (2 lines: Line 1 = XP Skills, Line 2 = Learn. Build. Innovate.) */}
              <h1 className="mt-4 sm:mt-5 text-[34px] sm:text-[42px] lg:text-[48px] xl:text-[54px] font-extrabold leading-[1.1] tracking-[-0.025em] text-white">
                <span className="block">XP Skills</span>
                <span className="block whitespace-nowrap">
                  <span>Learn. Build. </span>
                  <span className="text-[#0075FF]">Innovate.</span>
                </span>
              </h1>

              {/* Subtitle / Paragraph Description */}
              <p className="mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] sm:leading-[26px] text-[#8EA3BD] max-w-[490px]">
                XP Skills is a hands-on Robotics Project Lab where you work
                directly with real robotic systems, simulation tools and AI —
                guided by industry experts, not just lectures.
              </p>

              {/* CTA Button: Apply for the next cohort */}
              <div className="mt-8 sm:mt-9">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14.5px] sm:text-[15px] font-bold text-[#06152B] shadow-[0_0_24px_rgba(0,210,238,0.45)] hover:shadow-[0_0_36px_rgba(0,210,238,0.75)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  <span>Apply for the next cohort</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 2: WHY XP SKILLS (This is not a classroom.)
            Background: #FFFFFF
           ========================================================================= */}
        <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-white text-[#06152B]">
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            <div className="grid grid-cols-1 lg:grid-cols-[410px_1fr] xl:grid-cols-[440px_1fr] gap-10 lg:gap-12 xl:gap-16 items-center">
              {/* Left Column: Eyebrow, Headline & Description */}
              <div className="flex flex-col items-start max-w-[430px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  WHY XP SKILLS
                </p>

                <h2 className="mt-3.5 text-[28px] sm:text-[34px] lg:text-[38px] xl:text-[42px] font-extrabold leading-[1.14] tracking-tight text-[#06152B] whitespace-nowrap">
                  <span>This is not a </span>
                  <span className="text-[#0084FF]">classroom.</span>
                </h2>

                <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#51627C] max-w-[325px]">
                  XP Skills is built for builders. Work with real robotic
                  systems, modern simulation tools, and expert mentors to turn
                  concepts into working projects.
                </p>
              </div>

              {/* Right Column: 3 Feature Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-5.5">
                {WHY_XP_SKILLS_CARDS.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.title}
                      className="relative overflow-hidden rounded-[20px] p-6 sm:p-7 flex flex-col justify-between text-left bg-white border border-[#E2E8F0] shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_36px_rgba(37,99,235,0.09)] hover:-translate-y-1.5 transition-all duration-200 select-none group min-h-[235px]"
                    >
                      <div>
                        {/* Circular Icon Badge */}
                        <div
                          className="w-[48px] h-[48px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                          style={{ backgroundColor: card.iconBg }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: card.iconColor }}
                            strokeWidth={2.3}
                          />
                        </div>

                        {/* Title */}
                        <h3 className="mt-6 text-[17.5px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                          {card.title}
                        </h3>

                        {/* Description */}
                        <p className="mt-2.5 text-[13px] sm:text-[13.5px] leading-[21px] text-[#51627C]">
                          {card.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 3: PRODUCT CAPABILITIES (Build real skills for the real world.)
            Background: rgba(236, 245, 253, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  PRODUCT CAPABILITIES
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.12] tracking-tight text-[#06152B]">
                  <span className="block">Build real skills for</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D2EE] via-[#00BAEB] to-[#0090FF]">
                    the real world.
                  </span>
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  A structured, project-focused program that combines hardware,
                  simulation and AI, with guidance from experienced robotics
                  professionals.
                </p>
              </div>
            </div>

            {/* 6 Capability Cards Grid (2 rows of 3) */}
            <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {XP_SKILLS_CAPABILITIES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="rounded-[20px] p-6 sm:p-7 flex flex-col justify-between text-left shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_28px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-200 select-none group min-h-[225px]"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(226, 232, 240, 1)",
                    }}
                  >
                    <div>
                      {/* Circular Icon Badge */}
                      <div
                        className="w-[46px] h-[46px] rounded-full flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                        style={{ backgroundColor: item.iconBg }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.2}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-6 text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 4: A CLEAR LEARNING JOURNEY (From Learning to Building.)
            Background: Futuristic Blue Streaks Image (xp-skills-journey-bg.png)
           ========================================================================= */}
        <section className="relative w-full overflow-hidden py-20 sm:py-24 lg:py-28 bg-[#050913]">
          {/* Futuristic Blue Streaks Background Image provided by user */}
          <img
            src="/images/products/xp-skills-journey-bg.png"
            alt="Learning Journey Background"
            className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none opacity-85"
          />

          {/* Soft Dark Vignette / Contrast Overlays */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(5, 9, 19, 0.7) 0%, rgba(5, 9, 19, 0.35) 50%, rgba(5, 9, 19, 0.8) 100%)",
            }}
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[620px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#7E98B8]">
                  A CLEAR LEARNING JOURNEY
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[38px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-white">
                  From Learning to Building.
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#00D2EE] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#8EA3BD]">
                  A structured, hands-on journey that helps you go from
                  understanding the basics to building and demonstrating real
                  robotic systems.
                </p>
              </div>
            </div>

            {/* 4 Steps Timeline Flow */}
            <div className="mt-16 sm:mt-20 flex flex-col md:flex-row items-start justify-between gap-8 md:gap-3 lg:gap-5">
              {LEARNING_JOURNEY_STEPS.map((item, idx) => {
                const Icon = item.icon;
                const isLast = idx === LEARNING_JOURNEY_STEPS.length - 1;
                return (
                  <React.Fragment key={item.title}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full mx-auto md:mx-0 group select-none">
                      {/* Glowing Circle Icon Badge */}
                      <div
                        className="w-[66px] h-[66px] sm:w-[70px] sm:h-[70px] rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-105"
                        style={{
                          backgroundColor: item.iconBg,
                          boxShadow: `0 0 32px ${item.glowColor}`,
                        }}
                      >
                        <Icon
                          className="w-7 h-7"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.4}
                        />
                      </div>

                      {/* Step Title */}
                      <h3 className="mt-5 text-[18px] sm:text-[19px] font-bold text-white tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Step Description */}
                      <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[20px] text-[#8EA3BD]">
                        {item.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps */}
                    {!isLast && (
                      <div className="hidden md:flex items-center justify-center shrink-0 mt-[23px] text-[#00D2EE]">
                        <ArrowRight
                          className="w-6 h-6 text-[#00D2EE]"
                          strokeWidth={2.4}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 5: A PROGRAM FOR EVERY BUILDER
            Background: rgba(236, 245, 253, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            {/* Header Block: Left title + Right bordered description */}
            <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-[760px]">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#51627C]">
                  A PROGRAM FOR EVERY BUILDER
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[42px] xl:text-[46px] font-extrabold leading-[1.14] tracking-tight text-[#06152B]">
                  <span className="block whitespace-nowrap">
                    Build for <span className="text-[#00BAEB]">students,</span>
                  </span>
                  <span className="block whitespace-nowrap bg-gradient-to-r from-[#00D2EE] to-[#0075FF] bg-clip-text text-transparent">
                    Professionals and Innovators.
                  </span>
                </h2>
              </div>

              {/* Right Description with vertical separator bar */}
              <div className="max-w-[460px] border-l-2 border-[#CBD5E1] pl-5 sm:pl-6 py-1">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#51627C]">
                  Whether you are a student, working professional or robotics
                  enthusiast, XP Skills is designed for anyone who wants to
                  build real skills in robotics and Physical AI.
                </p>
              </div>
            </div>

            {/* 4 Cards Grid */}
            <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {XP_SKILLS_STAKEHOLDERS.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="overflow-hidden rounded-[20px] bg-white border border-[#E2E8F0] shadow-[0_2px_12px_rgba(0,0,0,0.03)] select-none flex flex-col"
                  >
                    {/* Card Top Image with Floating Icon */}
                    <div className="relative h-[165px] sm:h-[175px] w-full overflow-hidden bg-slate-100">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="h-full w-full object-cover"
                      />
                      {/* Frosted Glass Icon Badge */}
                      <div className="absolute top-3.5 left-3.5 w-[38px] h-[38px] rounded-full flex items-center justify-center bg-black/40 backdrop-blur-md border border-white/35 text-white shadow-sm">
                        <Icon className="w-[18px] h-[18px]" strokeWidth={2.2} />
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 sm:p-6 flex-1 flex flex-col justify-start">
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#06152B] tracking-tight leading-snug">
                        {card.title}
                      </h3>
                      <p className="mt-2 text-[13px] sm:text-[13.5px] leading-[20px] text-[#51627C]">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 6: REAL PROJECTS. TANGIBLE OUTCOMES.
            Background: rgba(255, 255, 255, 1)
           ========================================================================= */}
        <section
          className="relative w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 lg:gap-12">
              {/* Left Column: Heading & Description */}
              <div className="max-w-[460px] lg:max-w-[480px] shrink-0">
                <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                  PROJECTS
                </p>
                <h2 className="mt-3.5 text-[28px] sm:text-[32px] lg:text-[36px] xl:text-[40px] font-extrabold leading-[1.14] tracking-tight text-[#06152B]">
                  <span className="block whitespace-nowrap">
                    Real Projects. <span className="text-[#0075FF]">Tangible</span>
                  </span>
                  <span className="block text-[#00BAEB]">Outcomes.</span>
                </h2>
                <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[22px] sm:leading-[24px] text-[#51627C]">
                  Work on projects that mirror real-world applications and build
                  a portfolio that showcases your skills.
                </p>
              </div>

              {/* Right Column: 4 Project Cards in a Row */}
              <div className="flex-1 w-full grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-5 items-start">
                {XP_SKILLS_PROJECTS.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col items-center select-none"
                  >
                    {/* Card Image */}
                    <div className="w-full aspect-[1/0.92] rounded-[16px] overflow-hidden bg-slate-100 border border-slate-200/80 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Label below image */}
                    <h3 className="mt-3 text-[12.5px] sm:text-[13.5px] font-semibold text-[#51627C] text-center leading-snug">
                      {item.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            SECTION 7: CTA BANNER (Join the next cohort and build real-world skills.)
            Background Image: Student with Robotic Arm & Simulation Laptop
           ========================================================================= */}
        <section
          className="relative w-full overflow-hidden flex items-center py-20 sm:py-24 lg:py-28"
          style={{ backgroundColor: "#04111E", minHeight: "440px" }}
        >
          {/* Background Image provided by user */}
          <img
            src="/images/products/xp-skills-cta-bg.jpg"
            alt="Join the next cohort and build real-world skills"
            className="absolute inset-0 h-full w-full object-cover object-right select-none pointer-events-none z-0"
          />

          {/* Left-to-Right gradient overlay to ensure perfect readability across responsive breakpoints */}
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background:
                "linear-gradient(90deg, rgba(4, 17, 30, 0.95) 0%, rgba(4, 17, 30, 0.85) 38%, rgba(4, 17, 30, 0.2) 65%, transparent 100%)",
            }}
          />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[66px] xl:px-[72px]">
            <div className="max-w-[680px] flex flex-col items-start">
              {/* Headline */}
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] xl:text-[44px] font-extrabold leading-[1.16] tracking-tight text-white">
                <span className="block whitespace-nowrap">Join the next cohort and build</span>
                <span className="block whitespace-nowrap">real-world skills.</span>
              </h2>

              {/* Subtitle / Description */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#A0B3CC] max-w-[490px]">
                Be part of an expert-led Robotics / Physical AI program at XP
                Skills. Learn, build, and innovate with real hardware, simulation
                tools and industry experts.
              </p>

              {/* Action Button: Talk to Our Team */}
              <div className="mt-8 sm:mt-9">
                <button
                  onClick={openAgent}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 sm:px-8 py-3.5 text-[14.5px] sm:text-[15px] font-bold text-[#06152B] shadow-[0_0_28px_rgba(0,210,238,0.35)] hover:shadow-[0_0_40px_rgba(0,210,238,0.7)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer"
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight
                    className="h-4 w-4 text-[#06152B] transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default XpSkillsPage;
